import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
@Pipe({
  name: 'cron',
  standalone: true,
})
export class CronPipe implements PipeTransform {
  constructor(private translate: TranslateService) {}

  transform(cronExpression: string): string {
    if (!cronExpression) {
      return this.translate.instant('INVALIDCRONEXPRESSION');
    }

    const parts = cronExpression.split(' ');

    if (parts.length !== 5) {
      return this.translate.instant('INVALIDCRONFORMAT');
    }

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    // Validate month value
    if (
      month !== '*' &&
      (isNaN(Number(month)) || Number(month) < 1 || Number(month) > 12)
    ) {
      return this.translate.instant('UNSUPPORTEDFORMAT');
    }

    // Validate dayOfWeek value
    if (
      dayOfWeek !== '*' &&
      (isNaN(Number(dayOfWeek)) ||
        Number(dayOfWeek) < 0 ||
        Number(dayOfWeek) > 6)
    ) {
      return this.translate.instant('UNSUPPORTEDFORMAT');
    }

    // Validate dayOfMonth value
    if (
      dayOfMonth !== '*' &&
      (isNaN(Number(dayOfMonth)) ||
        Number(dayOfMonth) < 1 ||
        Number(dayOfMonth) > 31)
    ) {
      return this.translate.instant('UNSUPPORTEDFORMAT');
    }

    // Check for minute intervals like "*/5"
    if (minute.startsWith('*/')) {
      const interval = minute.substring(2);
      if (!isNaN(Number(interval))) {
        // Executes an operation every X minutes, where X is the value after */
        return `${this.translate.instant('EVERY')} ${interval} ${this.translate.instant('MINUTE')}`;
      } else {
        // Returns an error message if the minute format is invalid
        return this.translate.instant('INVALIDMINUTEFORMAT');
      }
    }

    // Check for specific "every hour at x minutes"
    if (hour === '*' && dayOfMonth === '*' && dayOfWeek === '*' && minute !== '0') {
      return `${this.translate.instant('EVERYHOUR')} ${this.translate.instant('AT')} ${minute} ${this.translate.instant('MINUTE')}`;
    }

    // Check for "every hour" case - 0 * * * *
    if (
      minute === '0' &&
      hour === '*' &&
      dayOfMonth === '*' &&
      dayOfWeek === '*' &&
      month === '*'
    ) {
      return this.translate.instant('EVERYHOUR');
    }

    // Specific day and month
    if (month !== '*' && dayOfMonth !== '*') {
      return `${this.translate.instant('EVERY')} ${this.getMonth(month)} ${this.translate.instant('ONDAY')} ${this.padZero(dayOfMonth)} ${this.translate.instant('AT')} ${this.padZero(hour)}:${this.padZero(minute)}h`;
    }

    // Specific day of the week
    if (dayOfWeek !== '*' && dayOfMonth === '*') {
      return `${this.translate.instant('EVERY')} ${this.getDayOfWeek(dayOfWeek)} ${this.translate.instant('AT')} ${this.padZero(hour)}:${this.padZero(minute)}h`;
    }

    // Every day at a specific time
    if (dayOfWeek === '*' && dayOfMonth === '*') {
      return `${this.translate.instant('EVERYDAYAT')} ${this.padZero(hour)}:${this.padZero(minute)}h`;
    }

    // Specific day of the month
    if (dayOfWeek === '*' && dayOfMonth !== '*') {
      return `${this.translate.instant('EVERYMONTHONDAY')} ${this.padZero(dayOfMonth)} ${this.translate.instant('AT')} ${this.padZero(hour)}:${this.padZero(minute)}h`;
    }

    // Unsupported format for other cases
    return this.translate.instant('UNSUPPORTEDFORMAT');
  }

  private getDayOfWeek(dayOfWeek: string): string {
    switch (dayOfWeek) {
      case '0':
        return this.translate.instant('SUNDAY');
      case '1':
        return this.translate.instant('MONDAY');
      case '2':
        return this.translate.instant('TUESDAY');
      case '3':
        return this.translate.instant('WEDNESDAY');
      case '4':
        return this.translate.instant('THURSDAY');
      case '5':
        return this.translate.instant('FRIDAY');
      case '6':
        return this.translate.instant('SATURDAY');
      default:
        return this.translate.instant('UNKNOWNDAY');
    }
  }

  private getMonth(month: string): string {
    switch (month) {
      case '1':
        return this.translate.instant('JANUARY');
      case '2':
        return this.translate.instant('FEBRUARY');
      case '3':
        return this.translate.instant('MARCH');
      case '4':
        return this.translate.instant('APRIL');
      case '5':
        return this.translate.instant('MAY');
      case '6':
        return this.translate.instant('JUNE');
      case '7':
        return this.translate.instant('JULY');
      case '8':
        return this.translate.instant('AUGUST');
      case '9':
        return this.translate.instant('SEPTEMBER');
      case '10':
        return this.translate.instant('OCTOBER');
      case '11':
        return this.translate.instant('NOVEMBER');
      case '12':
        return this.translate.instant('DECEMBER');
      default:
        return this.translate.instant('UNKNOWNMONTH');
    }
  }

  private padZero(value: string): string {
    return value.padStart(2, '0');
  }
}
